// @flow

import URLSearchParams from '@ungap/url-search-params';
import { useCallback, useState } from 'react';
import { getQueryParamValueOrCry } from '@tectonics/commons/query-params';
import { convertObjectIntoQueryString } from '@tectonics/commons/query-string';
import { convertDueDateFromAbbrevToFullYear } from '@tectonics/commons/card';
import { type Country, useCountry } from '@tectonics/commons/country';
import { type PublicIntegrationKey, usePublicIntegrationKey } from '@tectonics/commons/public-integration-key';
import { type Tenant, getApiBaseUrlByTenant, useTenant } from '@tectonics/commons/tenant';
import { CardTokenizationError } from './card-tokenization-error';
import { type VrUserInputValidations } from './user-input-validations';
import { type VrUserInputValues } from './user-input-values';

export function useVrAuthorizeSubmitHandlerWithStatus(
  vrUserInputValues: VrUserInputValues,
  vrUserInputValidations: VrUserInputValidations,
): [SubmitHandler, FormStatus, ?Error] {
  const [formStatus, setFormStatus] = useState<FormStatus>('initial');
  const [submitError, setSubmitError] = useState<?Error>(null);
  const handleVrAuthorizeSubmit = useVrAuthorizeSubmitHandler(vrUserInputValues);

  const vrAuthorizeSubmitHandlerWithStatus = useCallback(async (evt: SyntheticEvent<HTMLFormElement>) => {
    evt.preventDefault();

    const isInvalid = Object.values(vrUserInputValidations).some(Boolean);
    setFormStatus('validated');

    if (!isInvalid) {
      try {
        setFormStatus('submitting');
        setSubmitError(null);
        await handleVrAuthorizeSubmit(evt);
      } catch(error) {
        setSubmitError(error);
      } finally {
        setFormStatus('submitted');
      }
    }
  }, [vrUserInputValidations, setFormStatus, setSubmitError, handleVrAuthorizeSubmit]);

  return [vrAuthorizeSubmitHandlerWithStatus, formStatus, submitError];
}

type FormStatus =
  | 'initial'
  | 'validated'
  | 'submitting'
  | 'submitted'
;

export function useVrAuthorizeSubmitHandler(vrUserInputValues: VrUserInputValues): SubmitHandler {
  const country = useCountry();
  const publicIntegrationKey = usePublicIntegrationKey();
  const tenant = useTenant();

  const submitHandler = useCallback(async (evt: SyntheticEvent<HTMLFormElement>) => {
    evt.preventDefault();

    const tokenizedCard = await tokenizeCard(country, publicIntegrationKey, tenant, vrUserInputValues);
    const postParams = buildPostParams(vrUserInputValues, tokenizedCard);
    const submitResponse = await submit(postParams, tenant);
    window.location.replace(submitResponse.redirect_uri);

  }, [country, vrUserInputValues, tenant]);

  return submitHandler;
}

async function tokenizeCard(
  country: Country,
  publicIntegrationKey: PublicIntegrationKey,
  tenant: Tenant,
  vrUserInputValues: VrUserInputValues,
): Promise<TokenizedCard> {
  const url = getApiBaseUrlByTenant(tenant) + 'ws/token';

  const body = {
    card: {
      card_cvv: vrUserInputValues.cardCvv,
      card_name: vrUserInputValues.cardHolderName,
      card_number: vrUserInputValues.cardNumber,
      card_due_date: convertDueDateFromAbbrevToFullYear(vrUserInputValues.cardDueDate),
    },
    country: country.code,
    payment_type_code: 'vr',
    public_integration_key: publicIntegrationKey,
  };

  const data = {
    method: 'POST',
    headers: {
      'Content-Type': 'text/plain',
    },
    body: JSON.stringify(body),
  };

  const response = await fetch(url, data);
  const responseJson = await response.json();

  if (responseJson.status !== 'SUCCESS') {
    throw new CardTokenizationError();
  }

  const token = responseJson.token.token
    ? responseJson.token.token
    : responseJson.token;
  return { token };
}

type SubmitHandler = (evt: SyntheticEvent<HTMLFormElement>) => Promise<void>;

type TokenizedCard = {
  token: string,
};

function buildPostParams(vrUserInputValues: VrUserInputValues, tokenizedCard: TokenizedCard): PostParams {
  return {
    customer_document: vrUserInputValues.userBrazilianCpf,
    card_token: tokenizedCard.token,
  };
}

type PostParams = {
  state?: string,
  customer_document: string,
  card_token: string,
};

async function submit(postParams: PostParams, tenant: Tenant): Promise<SubmitResponse> {
  const response = await fetch(resolveAuthorizeSubmitUrl(tenant), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: convertObjectIntoQueryString(postParams),
  });

  if (!response) {
    throw new Error('Submit response is not defined');
  }

  const responseJson = await response.json();
  validateSubmitResponseJson(responseJson);

  return (responseJson: SubmitResponse);
}

type SubmitResponse = {
  redirect_uri: string,
};

function validateSubmitResponseJson(response: any) {
  if (!response) {
    throw new Error('Authorize submit response is not defined');
  }
  if (typeof response !== 'object') {
    throw new Error('Authorize submit response is not an object');
  }
  if (!response.redirect_uri) {
    throw new Error('Authorize response does not contain a redirect_uri');
  }
}

function resolveAuthorizeSubmitUrl(tenant: Tenant): URL {
  const params = buildAuthorizeSubmitQueryParams();
  const apiBaseUrl = getApiBaseUrlByTenant(tenant);
  return new URL(`uberpay/v1/payments/userflow/authorize/submit?${params.toString()}`, apiBaseUrl);
}

function buildAuthorizeSubmitQueryParams(): URLSearchParams {
  const state = getQueryParamValueOrCry('state');
  return new URLSearchParams({original_authorize_params: state});
}
